import { useEffect, useRef, useState } from "react";
import { Eraser, PenLine, Type } from "lucide-react";
import { Button } from "@/components/ui/button";

export const SignaturePad = ({ name = "", onChange }) => {
  const [mode, setMode] = useState("draw");
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  const ctx2d = () => {
    const c = canvasRef.current;
    if (!c) return null;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0D0D0D";
    return ctx;
  };

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: ((p.clientX - r.left) / r.width) * canvasRef.current.width, y: ((p.clientY - r.top) / r.height) * canvasRef.current.height };
  };

  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = ctx2d(); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ctx2d(); const { x, y } = pos(e);
    ctx.lineTo(x, y); ctx.stroke(); hasInk.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInk.current) onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasInk.current = false;
    onChange?.(null);
  };

  useEffect(() => {
    if (mode !== "type") return;
    if (!name.trim()) { onChange?.(null); return; }
    const c = document.createElement("canvas");
    c.width = 560; c.height = 160;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0D0D0D";
    ctx.font = "italic 52px 'Brush Script MT', 'Segoe Script', 'Snell Roundhand', cursive";
    ctx.textBaseline = "middle";
    ctx.fillText(name.trim(), 24, 84);
    onChange?.(c.toDataURL("image/png"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, name]);

  return (
    <div data-testid="signature-pad">
      <div className="mb-2 flex gap-2">
        <Button type="button" size="sm" variant={mode === "draw" ? "default" : "outline"} className="gap-1.5"
          onClick={() => { setMode("draw"); onChange?.(null); }} data-testid="sigpad-mode-draw">
          <PenLine className="h-3.5 w-3.5" /> Draw
        </Button>
        <Button type="button" size="sm" variant={mode === "type" ? "default" : "outline"} className="gap-1.5"
          onClick={() => setMode("type")} data-testid="sigpad-mode-type">
          <Type className="h-3.5 w-3.5" /> Type
        </Button>
        {mode === "draw" && (
          <Button type="button" size="sm" variant="ghost" className="ml-auto gap-1.5 text-muted-foreground" onClick={clear} data-testid="sigpad-clear">
            <Eraser className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
      </div>
      {mode === "draw" ? (
        <canvas ref={canvasRef} width={560} height={160}
          className="h-[120px] w-full cursor-crosshair touch-none rounded-lg border border-dashed bg-white"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="sigpad-canvas" />
      ) : (
        <div className="flex h-[120px] items-center rounded-lg border border-dashed bg-white px-6" data-testid="sigpad-typed-preview">
          <span className="text-4xl text-slate-900" style={{ fontFamily: "'Brush Script MT','Segoe Script','Snell Roundhand',cursive", fontStyle: "italic" }}>
            {name.trim() || "Your name appears here"}
          </span>
        </div>
      )}
      <p className="mt-1.5 text-[11px] text-muted-foreground">Your signature is stamped onto the executed PDF with a timestamp, IP address and verification ID.</p>
    </div>
  );
};
